/**
 * useSessionManager
 *
 * Coordinates Wadakaroo's keep-alive + auto-logout behaviour:
 *
 *  1. Keep the session alive while the user is actively working by
 *     proactively refreshing the JWT a couple of minutes before its
 *     `exp` claim. The user never sees a 401 mid-action.
 *
 *  2. Auto-logout after 15 minutes of inactivity, with a 60-second
 *     warning modal at the 14-minute mark so the user can extend.
 *
 *  3. Coordinate across browser tabs:
 *       - Activity in any tab counts as activity in all tabs (shared
 *         timestamp in localStorage).
 *       - Logout in any tab logs out every other tab (logout broadcast
 *         + access-token-removed detection on the `storage` event).
 *       - When one tab refreshes the token, all other tabs pick up the
 *         new token automatically (no double-refresh).
 *
 * Mount this hook ONCE inside an authenticated boundary. The recommended
 * spot in App.jsx is inside the <ProtectedRoute> wrapper so it runs for
 * every protected page but not for /login or /register.
 *
 * The hook returns a small state object the warning modal consumes:
 *   { warningOpen, secondsLeft, dismissWarning }
 *
 * Tunables live at the top of the file. Change once, propagate everywhere.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    LAST_ACTIVITY_KEY,
    LOGOUT_BROADCAST_KEY,
    ACCESS_TOKEN_STORAGE_KEY,
    getAccessToken,
    getRefreshToken,
    getLastActivity,
    setLastActivity,
    getTokenExpiration,
    setTokens,
    parseToken,
    setUser as persistUserToStorage,
    isTokenExpired,
} from '../services/authService';
import { refreshTokenApi } from '../services/api';
import {
    logoutUser,
    selectIsAuthenticated,
    selectPasswordResetRequired,
} from '../store/slices/authSlice';

// ============================================
// Tunables
// ============================================

const IDLE_LIMIT_MS              = 15 * 60 * 1000;  // logout at 15 min idle
const WARNING_AT_MS              = 14 * 60 * 1000;  // warning at 14 min idle
const WARNING_COUNTDOWN_SEC      = 60;              // shown in the modal

// How often the listeners actually persist an activity write. Each write
// triggers a storage event in every other tab, so we throttle aggressively.
const ACTIVITY_THROTTLE_MS       = 15 * 1000;

// How often the idle/refresh check fires. Cheap (one Date.now() compare).
const CHECK_INTERVAL_MS          = 5 * 1000;

// How far before token `exp` the proactive refresh fires. The access token
// is currently 1 hour (see application.yml → access-token.expiration: 3600);
// refreshing 2 min early gives a generous safety margin.
const REFRESH_BEFORE_EXP_MS      = 2 * 60 * 1000;

// Minimum gap between successive proactive refresh attempts even if the
// scheduler "thinks" it should fire. Prevents a tight loop if the backend
// keeps returning a token that's already near expiry.
const MIN_REFRESH_INTERVAL_MS    = 30 * 1000;

// Events that count as "the user is still here". Pointer/keyboard/scroll/
// touch — deliberately not focus/blur (those fire on tab switches even
// when the user is idle on the other tab).
const ACTIVITY_EVENTS = [
    'mousemove',
    'mousedown',
    'keydown',
    'click',
    'scroll',
    'touchstart',
    'wheel',
];

// ============================================
// Hook
// ============================================

export default function useSessionManager() {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const passwordResetRequired = useSelector(selectPasswordResetRequired);

    // Warning modal state — drives <InactivityWarningModal />.
    const [warningOpen, setWarningOpen]   = useState(false);
    const [secondsLeft, setSecondsLeft]   = useState(WARNING_COUNTDOWN_SEC);

    // Refs avoid re-renders. The hook should be effectively side-effect-only.
    const lastActivityWriteRef = useRef(0);   // throttle guard for storage writes
    const lastRefreshAtRef     = useRef(0);   // throttle guard for refresh calls
    const isRefreshingRef      = useRef(false);

    // Whether the hook is "live" — i.e. user is authenticated AND not in the
    // restricted password-reset flow. The reset flow has its own scoped token
    // that can't even hit /auth/token usefully, so timers there are pointless.
    const enabled = isAuthenticated && !passwordResetRequired;

    // --------------------------------------------------------------
    // Helper: trigger a logout with a reason. Centralised so all the
    // teardown paths (inactivity, broadcast, refresh fail) look the same.
    // --------------------------------------------------------------
    const triggerLogout = useCallback((reason) => {
        // Already torn down? Don't double-dispatch.
        if (!getAccessToken()) {
            setWarningOpen(false);
            return;
        }
        setWarningOpen(false);
        // skipApi for cross_tab: the originating tab already hit /auth/logout
        // server-side; firing it again with a token we're about to drop would
        // just produce a 401 in the console.
        const skipApi = reason === 'cross_tab';
        // skipBroadcast for cross_tab: we're REACTING to a broadcast, not
        // initiating one. Re-broadcasting would echo back to the original tab.
        const skipBroadcast = reason === 'cross_tab';
        dispatch(logoutUser({ reason, skipApi, skipBroadcast }));
    }, [dispatch]);

    // --------------------------------------------------------------
    // Activity tracking — listeners write a shared timestamp.
    // --------------------------------------------------------------
    useEffect(() => {
        if (!enabled) return;

        // Seed the activity timestamp on mount so a tab that's just been
        // authenticated doesn't immediately think it's been idle for hours.
        setLastActivity();

        const handleActivity = () => {
            const now = Date.now();
            // Throttle: only one storage write per ACTIVITY_THROTTLE_MS, no
            // matter how many events fire. mousemove fires hundreds of times
            // per second on a track-pad.
            if (now - lastActivityWriteRef.current < ACTIVITY_THROTTLE_MS) {
                return;
            }
            lastActivityWriteRef.current = now;
            setLastActivity(now);

            // If the warning modal is open and the user genuinely interacts,
            // close it. The 15-min idle check will see fresh activity and
            // back off.
            setWarningOpen((open) => (open ? false : open));
        };

        ACTIVITY_EVENTS.forEach((ev) => {
            // `passive: true` keeps scroll/touch perf snappy.
            window.addEventListener(ev, handleActivity, { passive: true });
        });

        return () => {
            ACTIVITY_EVENTS.forEach((ev) => {
                window.removeEventListener(ev, handleActivity);
            });
        };
    }, [enabled]);

    // --------------------------------------------------------------
    // Cross-tab sync via the `storage` event.
    //
    // Three things to watch for:
    //   - logout broadcast      → tear this tab down
    //   - access token removed  → tear this tab down (someone called clearAuth)
    //   - access token replaced → already refreshed elsewhere, nothing to do
    //                              (parseToken on next request reads the new
    //                              one straight from localStorage)
    // --------------------------------------------------------------
    useEffect(() => {
        if (!enabled) return;

        const handleStorage = (e) => {
            if (e.key === LOGOUT_BROADCAST_KEY && e.newValue) {
                console.log('🔁 Logout broadcast received from another tab');
                triggerLogout('cross_tab');
                return;
            }
            if (e.key === ACCESS_TOKEN_STORAGE_KEY && !e.newValue) {
                console.log('🔁 Access token cleared in another tab');
                triggerLogout('cross_tab');
                return;
            }
            // Token UPDATED in another tab — no action needed, but reset
            // the local refresh throttle so this tab doesn't immediately
            // try to refresh a token that's already fresh.
            if (e.key === ACCESS_TOKEN_STORAGE_KEY && e.newValue) {
                lastRefreshAtRef.current = Date.now();
            }
        };

        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [enabled, triggerLogout]);

    // --------------------------------------------------------------
    // Proactive refresh — fired from the unified tick below.
    // --------------------------------------------------------------
    const maybeRefresh = useCallback(async () => {
        if (isRefreshingRef.current) return;

        const exp = getTokenExpiration();
        if (!exp) return;

        const msUntilExp = exp - Date.now();

        // Token already expired? The axios interceptor will handle it on the
        // next request. No point firing a proactive refresh now.
        if (msUntilExp <= 0) return;

        // Outside the refresh window — do nothing.
        if (msUntilExp > REFRESH_BEFORE_EXP_MS) return;

        // Recently refreshed by us OR by another tab — back off.
        if (Date.now() - lastRefreshAtRef.current < MIN_REFRESH_INTERVAL_MS) return;

        // If the user is already idle past the limit, don't refresh — they're
        // about to be logged out anyway, and refreshing now would just extend
        // a session we want to end.
        const idleMs = Date.now() - getLastActivity();
        if (idleMs >= IDLE_LIMIT_MS) return;

        const refreshToken = getRefreshToken();
        if (!refreshToken) return;

        isRefreshingRef.current = true;
        lastRefreshAtRef.current = Date.now();

        try {
            console.log('⏱️ Proactive token refresh');
            const data = await refreshTokenApi(refreshToken);
            const { accessToken, refreshToken: newRefreshToken } = data;
            setTokens(accessToken, newRefreshToken);
            const user = parseToken(accessToken);
            if (user) {
                persistUserToStorage(user);
            }
            console.log('✅ Proactive refresh succeeded');
        } catch (err) {
            // Refresh token rejected (expired, revoked, etc) — hard logout.
            // 401 here is terminal: there's no "try the refresh again" move.
            console.error('❌ Proactive refresh failed:', err?.response?.status);
            triggerLogout('refresh_failed');
        } finally {
            isRefreshingRef.current = false;
        }
    }, [triggerLogout]);

    // --------------------------------------------------------------
    // The unified tick — checks idle state AND refresh window every
    // CHECK_INTERVAL_MS. One interval, both responsibilities.
    // --------------------------------------------------------------
    useEffect(() => {
        if (!enabled) return;

        const tick = () => {
            // Safety: if the token vanished or fully expired between ticks
            // (e.g. another tab logged out, or system clock jump), bail.
            const token = getAccessToken();
            if (!token) {
                triggerLogout('cross_tab');
                return;
            }
            if (isTokenExpired(token, 0)) {
                // Token is past `exp` with zero buffer. The interceptor will
                // catch the next request, but we may as well prompt logout
                // now if we can't refresh.
                const refreshToken = getRefreshToken();
                if (!refreshToken) {
                    triggerLogout('refresh_failed');
                    return;
                }
                // Otherwise fall through — maybeRefresh below will handle.
            }

            const idleMs = Date.now() - getLastActivity();

            if (idleMs >= IDLE_LIMIT_MS) {
                triggerLogout('inactivity');
                return;
            }

            if (idleMs >= WARNING_AT_MS) {
                // Compute seconds left until the 15-min mark and feed the
                // modal. Clamping at 0 protects against a system clock jump.
                const secs = Math.max(0, Math.ceil((IDLE_LIMIT_MS - idleMs) / 1000));
                setSecondsLeft(secs);
                setWarningOpen(true);
            } else if (warningOpen) {
                // Below the warning threshold again (user came back). Close.
                setWarningOpen(false);
            }

            // Fire proactive refresh in the same tick. It's a no-op outside
            // the refresh window.
            maybeRefresh();
        };

        // Run once immediately so we don't wait a full CHECK_INTERVAL_MS
        // after mount before the first idle check / refresh attempt.
        tick();

        const id = setInterval(tick, CHECK_INTERVAL_MS);
        return () => clearInterval(id);
    }, [enabled, triggerLogout, maybeRefresh, warningOpen]);

    // --------------------------------------------------------------
    // Public API for the warning modal.
    // --------------------------------------------------------------

    /**
     * "Stay signed in" button. Resets the activity timer everywhere
     * (so all tabs back off the warning) and closes the modal.
     */
    const dismissWarning = useCallback(() => {
        setLastActivity();
        lastActivityWriteRef.current = Date.now();
        setWarningOpen(false);
    }, []);

    /**
     * "Sign out now" button on the modal — same path as the avatar
     * menu logout, just initiated from the warning instead.
     */
    const signOutNow = useCallback(() => {
        triggerLogout('manual');
    }, [triggerLogout]);

    return {
        warningOpen,
        secondsLeft,
        dismissWarning,
        signOutNow,
    };
}